import { conditionallyAddPlus, filterQuote } from 'utils/selfServeHelpers';
import { CreateAndChargeNewOrgCardInput, QuoteInput, SelfServeHelpInput } from 'globalTypes';
import CREATE_AND_CHARGE_NEW_ORG_CARD_MUTATION from 'admin/mutations/CreateAndChargeNewOrgCard.gql';
import CREATE_SELF_SERVE_HELP_REQUEST_MUTATION from 'admin/mutations/CreateSelfServeHelpRequest.gql';
import CREATE_SELF_SERVE_QUOTE_MUTATION from 'admin/mutations/CreateSelfServeQuote.gql';
import UPDATE_SELF_SERVE_QUOTE_MUTATION from 'admin/mutations/UpdateSelfServeQuote.gql';
import {
  UpgradePlanQuote,
  UpgradePlanData,
} from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import RemindAPI from 'lib/RemindAPI';

import {
  CreateAndChargeNewOrgCard,
  CreateAndChargeNewOrgCardVariables,
} from 'admin/mutations/__generated__/CreateAndChargeNewOrgCard';

import {
  SelfServeRequestHelpMutation,
  SelfServeRequestHelpMutationVariables,
} from 'admin/mutations/__generated__/SelfServeRequestHelpMutation';

import { CreateQuote, CreateQuoteVariables } from 'admin/mutations/__generated__/CreateQuote';

import {
  UpdateQuote,
  UpdateQuoteVariables,
  UpdateQuote_updateQuote_error,
} from 'admin/mutations/__generated__/UpdateQuote';

type UpdateQuoteError = UpdateQuote_updateQuote_error;

type SuccessResponse<T> = { success: true } & T;
type ErrorResponse<E> = { success: false } & E;
export type ActionResponse<T, E = { errorMessage: string }> = SuccessResponse<T> | ErrorResponse<E>;
export type QuotePayload = { quote: UpgradePlanQuote };

type SelfServeQuoteError = {
  fieldErrors: { [key: string]: string };
  errorMessage: string;
};

export function logSelfServeError(organizationUuid: String, consoleErrorText: String) {
  // Rollbar uses the 'AdminSelfServe' to determine if it should notify errors to #self-serve-errors
  // channel in slack.  If you change this tag, change it there too.
  console.error(`AdminSelfServe - organization='${organizationUuid}' error='${consoleErrorText}'`);
}

export async function createSelfServeHelpRequest(
  input: SelfServeHelpInput
): Promise<ActionResponse<{}>> {
  const { sendSelfServeHelpRequest: resp } = await RemindAPI.execute<
    SelfServeRequestHelpMutation,
    SelfServeRequestHelpMutationVariables
  >({
    mutation: CREATE_SELF_SERVE_HELP_REQUEST_MUTATION,
    variables: { input },
  });

  if (resp.error) {
    logSelfServeError(input.organizationUuid, 'Failed to create help request');
    return {
      success: false,
      errorMessage: 'There was a problem submitting your help request. Please try again',
    };
  }

  return { success: true };
}

const errorRemap: { [key: string]: { field: string; message: string } } = {
  requester_phone_number: {
    field: 'phone_number',
    message: 'Phone Number is Invalid',
  },
  organization_postal_code: {
    field: 'postal_code',
    message: 'Postal Code is Invalid',
  },
  invalid_address: {
    field: 'postal_code',
    message: 'State and Postal Code do not match',
  },
  organization: {
    field: 'organization',
    message: 'Organization is not valid',
  },
};

export async function createSelfServeQuote(
  input: QuoteInput
): Promise<ActionResponse<QuotePayload, SelfServeQuoteError>> {
  const genericErrorMessage =
    'There was a problem submitting your request for a quote. Please try again';

  try {
    const { createQuote: resp } = await RemindAPI.execute<CreateQuote, CreateQuoteVariables>({
      mutation: CREATE_SELF_SERVE_QUOTE_MUTATION,
      variables: { input },
    });

    if (resp.error) {
      const errorMap = resp.error.fieldErrors.reduce((agg, e) => {
        const remap = errorRemap[e.field];
        return remap ? { ...agg, [remap.field]: remap.message } : agg;
      }, {} as { [key: string]: string });

      const consoleError =
        errorMap && Object.values(errorMap).length
          ? Object.values(errorMap).join(', ')
          : resp.error?.message;
      logSelfServeError(input.organizationUuid, consoleError ?? 'unknown error from gql');
      return { success: false, errorMessage: genericErrorMessage, fieldErrors: errorMap };
    }

    if (!resp.quote) {
      logSelfServeError(input.organizationUuid, 'Unknown error - no quote');
      return { success: false, errorMessage: genericErrorMessage, fieldErrors: {} };
    }

    return { success: true, quote: filterQuote(resp.quote) };
  } catch {
    logSelfServeError(input.organizationUuid, 'Unknown error - CreateQuote threw an unknown error');
    return { success: false, errorMessage: genericErrorMessage, fieldErrors: {} };
  }
}

export function filterQuoteUpdates(quote: UpgradePlanQuote, plan: UpgradePlanData): QuoteInput {
  return {
    organizationUuid: quote.organization.uuid,
    studentsCount: quote.studentsCount,
    teachersCount: quote.teachersCount,
    startDate: quote.startDate,
    organizationAddress: quote.organizationAddress,
    billingFirstName: plan.billingFirstName ?? plan.requesterFirstName,
    billingLastName: plan.billingLastName ?? plan.requesterLastName,
    billingEmail: plan.billingEmail ?? plan.requesterEmail,
    billingPhoneNumber: conditionallyAddPlus(plan.phoneNumber!),
    requesterFirstName: plan.requesterFirstName ?? plan.billingFirstName,
    requesterLastName: plan.requesterLastName ?? plan.billingLastName,
    requesterEmail: plan.requesterEmail ?? plan.billingEmail,
    requesterPhoneNumber: conditionallyAddPlus(plan.phoneNumber!),
    taxExempt: plan.taxExempt,
  };
}

export async function updateQuote(
  quoteUuid: string,
  input: QuoteInput
): Promise<ActionResponse<QuotePayload>> {
  const errorText = `AdminUpgradePlanActions - could not update self serve quote quote=${quoteUuid} org=${input.organizationUuid}`;
  try {
    const {
      updateQuote: { quote: updatedQuote, error },
    } = await RemindAPI.execute<UpdateQuote, UpdateQuoteVariables>({
      mutation: UPDATE_SELF_SERVE_QUOTE_MUTATION,
      variables: { uuid: quoteUuid, input },
    });

    if (error || !updatedQuote) {
      logSelfServeError(input.organizationUuid, error?.message ?? 'unknown error updating quote');
      return {
        success: false,
        errorMessage: error?.message ?? 'There was a problem updating your quote. Please try again',
      };
    } else {
      return { success: true, quote: filterQuote(updatedQuote) };
    }
  } catch (err) {
    const error = err as UpdateQuoteError;
    console.warn(error);
    logSelfServeError(input.organizationUuid, errorText);
    return { success: false, errorMessage: err.message };
  }
}

export function convertSelfServeLead(
  sourceId: string,
  organizationUuid: string,
  organizationId: number,
  price: number,
  quoteUuid: string
) {
  const mutationInput = {
    orgUuid: organizationUuid,
    orgId: organizationId,
    stripeSourceId: sourceId,
    amount: price,
    quoteUuid,
  } as CreateAndChargeNewOrgCardInput;
  const errorText = `AdminUpgradePlanActions - could not charge card for self serve quote quote=${quoteUuid} org=${organizationUuid}`;

  return RemindAPI.execute<CreateAndChargeNewOrgCard, CreateAndChargeNewOrgCardVariables>({
    mutation: CREATE_AND_CHARGE_NEW_ORG_CARD_MUTATION,
    variables: { input: mutationInput },
  })
    .then(({ createAndChargeNewOrgCard: resp }) => {
      if (resp.error) {
        console.warn(resp.error.message);
        logSelfServeError(organizationUuid, errorText);
        return { success: false, errorMessage: resp.error.message };
      } else if (resp.declineReason) {
        console.warn(resp.declineReason);
        logSelfServeError(organizationUuid, errorText);
        return { success: false, declineReason: resp.declineReason };
      } else {
        return { success: true, declineReason: null, errorMessage: null };
      }
    })
    .catch(err => {
      console.warn(err);
      logSelfServeError(organizationUuid, errorText);
      return { success: false, declineReason: null, errorMessage: err.message };
    });
}
